import { isAndroidInvoice, saveNativeInvoice } from './invoicePdfNative';
import { preparePhoto } from 'rentalui-photo-upload';
import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
export const VERTICAL = 'real_estate';

export const client = axios.create({ baseURL: API_BASE_URL });



// Demo images ship as local static assets in this app's own public/images/dummy (same relative
// paths rentalapi's seed data uses) rather than being fetched cross-origin from rentalapi - that
// avoided rentalapi's Cross-Origin-Resource-Policy header blocking <img> loads, and matches how
// TestApplication's microfrontends served images. process.env.PUBLIC_URL is always '' (no
// "homepage" field - this app is served from the root of its own subdomain in both local dev and
// production), so this just resolves to a root-relative path. Swap this for real cloud image
// URLs later.
//
// Owner-uploaded photos (see uploadImages() below) are the one exception - those are real files
// that only exist on rentalapi (routes/uploads.js), not shipped as static assets here, so they
// resolve against API_BASE_URL instead. rentalapi opts /uploads specifically out of its default
// same-origin Cross-Origin-Resource-Policy so these <img> loads aren't blocked (see app.js).
export function imageUrl(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('/uploads/')) return `${API_BASE_URL}${path}`;
  return encodeURI(`${process.env.PUBLIC_URL}${path}`);
}

// UPLOAD_Images_to_GCS.md ("faster images") - an uploaded photo is stored as a WebP ladder
// `<base>_320.webp` .. `_2560.webp` and the canonical `_1024` URL is what lands in images[].
// Given any stored image URL, return a `srcSet` spanning those widths so the browser fetches
// only the size it needs; '' for a legacy image (plain .jpg/.png, or a bundled asset) so the
// caller just renders a plain <img src>. Keep VARIANT_WIDTHS in sync with routes/uploads.js.
const VARIANT_WIDTHS = [320, 640, 1024, 1600, 2560];
export function imageSrcSet(url) {
  if (!url) return '';
  const m = /^(.*)_\d+\.webp$/.exec(url);
  if (!m) return '';
  return VARIANT_WIDTHS.map((w) => `${imageUrl(`${m[1]}_${w}.webp`)} ${w}w`).join(', ');
}

// Owner uploads listing photos (multipart/form-data) and gets back URLs to put straight into a
// property's `images` array - see pages/PropertyForm.js. Don't set a Content-Type header manually
// here; letting axios/the browser generate it (including the multipart boundary) from the
// FormData instance is required for the server to parse it.
export async function uploadImages(files) {
  const formData = new FormData();
  for (const file of files) {
    formData.append('images', await preparePhoto(file));
  }
  const res = await client.post('/api/uploads/images', formData);
  return res.data.data;
}

// ---- Public browsing ----

export async function listProperties(params = {}) {
  const res = await client.get('/api/real_estate/properties', { params });
  return res.data; // { data, pagination }
}

export async function getProperty(id) {
  const res = await client.get(`/api/real_estate/properties/${id}`);
  return res.data.data;
}

// ---- fix_issue3.md ----
// The generic /api/:vertical/items router is mounted for real_estate too (properties are
// RentalItem docs), so these work against the property id.

// #7 - "You may also like": other live properties, same category first.
export async function getRelatedItems(id) {
  const res = await client.get(`/api/${VERTICAL}/items/${id}/related`);
  return res.data.data;
}

// #3 - APPROVED ratings shown on a listing (public).
export async function getItemRatings(id) {
  const res = await client.get(`/api/${VERTICAL}/items/${id}/ratings`);
  return res.data.data;
}

// #3 - a logged-in customer with a completed visit/rental rates the property. Held for admin
// approval server-side.
export async function submitRating(id, { rating, review } = {}) {
  const res = await client.post(`/api/${VERTICAL}/items/${id}/ratings`, { rating, review });
  return res.data;
}

// ---- Owner ----

export async function getMyProperties() {
  const res = await client.get('/api/real_estate/properties/mine');
  return res.data.data;
}

export async function createProperty(payload) {
  const res = await client.post('/api/real_estate/properties', payload);
  return res.data.data;
}

export async function updateProperty(id, payload) {
  const res = await client.patch(`/api/real_estate/properties/${id}`, payload);
  return res.data.data;
}

export async function deleteProperty(id) {
  const res = await client.delete(`/api/real_estate/properties/${id}`);
  return res.data;
}

// Owner/admin: replace the property's blocked date ranges (renovation / owner use / holiday) -
// common/v7 availability, same shape as the other verticals' PUT .../blocked-dates.
export async function saveBlockedDates(propertyId, blockedDates) {
  const res = await client.put(`/api/real_estate/properties/${propertyId}/blocked-dates`, { blockedDates });
  return res.data.data;
}

// StocksManagement doc §21/§23 - owner/admin read-only view of a property's inventory: capacity +
// the InventoryTransaction audit trail.
export async function getInventory(propertyId) {
  const res = await client.get(`/api/real_estate/properties/${propertyId}/inventory`);
  return res.data.data;
}

// ---- Visits ----

// Owner Bookings & Cancellation v2 doc, Story 6 - checked before submitting so a slot someone
// else already booked shows as unavailable instead of the customer finding out only after a 409.
export async function checkVisitAvailability(propertyId, visitDate) {
  const res = await client.get(`/api/real_estate/properties/${propertyId}/visit-availability`, { params: { visitDate } });
  return res.data.data.available;
}

export async function createVisit(payload) {
  const res = await client.post('/api/real_estate/visits', payload);
  return res.data; // { msg, data }
}

export async function getMyVisits() {
  const res = await client.get('/api/real_estate/visits/mine');
  return res.data.data;
}

export async function updateVisitStatus(id, status) {
  const res = await client.patch(`/api/real_estate/visits/${id}`, { status });
  return res.data.data;
}

export async function sendVisitReminder(id) {
  const res = await client.post(`/api/real_estate/visits/${id}/remind`);
  return res.data;
}

// ---- Customer self-service (RealEstateVisit_user_stories.md stories 3-4) ----

// Owner Bookings & Cancellation v2 doc, Story 1 - reason is required. Visits are free scheduling
// (no rentalAmount), so unlike the other 3 verticals' cancelBooking there's no refund breakdown
// to preview here - see routes/realEstate.js's cancel handler.
export async function cancelVisit(id, reason) {
  const res = await client.patch(`/api/real_estate/visits/${id}/cancel`, { reason });
  return res.data;
}

export async function requestReschedule(id, payload) {
  const res = await client.post(`/api/real_estate/visits/${id}/reschedule`, payload);
  return res.data;
}

// ---- Owner actions (RealEstateVisit_user_stories.md stories 2-3) ----

export async function resolveReschedule(id, status) {
  const res = await client.patch(`/api/real_estate/visits/${id}/reschedule`, { status });
  return res.data.data;
}

export async function completeVisit(id, remarks = '') {
  const res = await client.patch(`/api/real_estate/visits/${id}/complete`, { remarks });
  return res.data.data;
}

// ---- Site-visit agents (03_requirements_missing.md stories 3-5) ----
// "Agent" == "delivery person" for real estate (reuses the `delivery` role + the shared
// deliveryAssignment sub-document). area/date rank the candidate list, same as jewelry's
// AssignDeliveryModal.

export async function getVisitAgents(area, date) {
  const res = await client.get('/api/real_estate/agents', { params: { area, date } });
  return res.data.data;
}

export async function assignVisitAgent(id, payload) {
  const res = await client.patch(`/api/real_estate/visits/${id}/assign-agent`, payload);
  return res.data.data;
}

export async function agentDeclineVisit(id, reason) {
  const res = await client.patch(`/api/real_estate/visits/${id}/agent-decline`, { reason });
  return res.data.data;
}

// The agent MUST record what happened on the visit (03_requirements_missing.md) - `remarks` is
// required by the backend. `outcome` is an optional quick tag.
export async function agentCompleteVisit(id, remarks, outcome = '') {
  const res = await client.patch(`/api/real_estate/visits/${id}/agent-complete`, { remarks, outcome });
  return res.data.data;
}

export async function getAssignedVisits() {
  const res = await client.get('/api/real_estate/visits/assigned');
  return res.data.data;
}

// ---- Agent onboarding requests (owner -> website owner) ----
export async function requestAgent(payload) {
  const res = await client.post('/api/real_estate/agent-requests', payload);
  return res.data.data;
}

export async function listMyAgentRequests() {
  const res = await client.get('/api/real_estate/agent-requests');
  return res.data.data;
}

export async function adminListAgentRequests(status) {
  const res = await client.get('/api/admin/real_estate/agent-requests', { params: status ? { status } : {} });
  return res.data.data;
}

export async function adminDecideAgentRequest(id, decision, reviewerNote) {
  const res = await client.patch(`/api/admin/real_estate/agent-requests/${id}`, { decision, reviewerNote });
  return res.data; // { data, createdUser?, tempPassword? }
}

// ---- Google Maps address search (server-side proxy - no key in the browser) ----
// Used by AddressAutocomplete. All three degrade to empty/null when rentalapi has no
// GOOGLE_MAPS_API_KEY, so the caller just falls back to plain typed fields.
export async function addressAutocomplete(q, sessionToken) {
  const res = await client.get('/api/maps/autocomplete', { params: { q, sessionToken } });
  return res.data.data?.predictions || [];
}

export async function addressPlaceDetails(placeId, sessionToken) {
  const res = await client.get(`/api/maps/place/${encodeURIComponent(placeId)}`, { params: { sessionToken } });
  return res.data.data;
}

export async function addressReverseGeocode(lat, lng) {
  const res = await client.get('/api/maps/reverse-geocode', { params: { lat, lng } });
  return res.data.data;
}

// ---- Admin ----

// status: 'active' (default - excludes disabled) | 'disabled' | 'all'
export async function adminListProperties(status) {
  const res = await client.get('/api/admin/real_estate/properties', { params: status ? { status } : {} });
  return res.data.data;
}

// The website owner changes a listing's status (disable-not-delete) or edits its details.
export async function adminUpdateProperty(id, payload) {
  const res = await client.patch(`/api/admin/real_estate/properties/${id}`, payload);
  return res.data.data;
}

export async function adminDeleteProperty(id) {
  const res = await client.delete(`/api/admin/real_estate/properties/${id}`);
  return res.data;
}

export async function adminListVisits() {
  const res = await client.get('/api/admin/real_estate/visits');
  return res.data.data;
}

export async function adminListUsers() {
  const res = await client.get('/api/admin/real_estate/users');
  return res.data.data;
}

// Manual toggle since there's no payment gateway yet (RealEstateVisit_user_stories_v2.md story
// 10 / same gap as the admin-bootstrap script in Application/LoginAndUser).
export async function adminSetPlatformFee(userId, paid) {
  const res = await client.patch(`/api/admin/real_estate/users/${userId}/platform-fee`, { paid });
  return res.data.data;
}

// Admin-created accounts for roles that aren't self-serve via signup (delivery/operations staff
// and the 3 finance_* roles - Owner Bookings & Cancellation v2 doc's "New roles").
export async function adminCreateUser(payload) {
  const res = await client.post('/api/admin/real_estate/users', payload);
  return res.data.data;
}

// ---- Delivery / agent payables (delivery_person_payables.md) ----
// real_estate has its own agent/owner routes; the finance review side reuses the shared
// /api/admin/delivery-payables. Fn names match the shared DeliveryPayablesPanel / OwnerPayablesPanel.
export async function getMyDeliveryPayables(status) {
  const res = await client.get('/api/real_estate/agent-payables', { params: status ? { status } : {} });
  return res.data; // { data, summaryByOwner }
}
export async function remindOwnerPayable(id) {
  const res = await client.post(`/api/real_estate/agent-payables/${id}/remind`);
  return res.data.data;
}
export async function escalatePayable(id, note) {
  const res = await client.post(`/api/real_estate/agent-payables/${id}/escalate`, { note });
  return res.data.data;
}
export async function getOwedDeliveryPayables(status) {
  const res = await client.get('/api/real_estate/delivery-payables', { params: status ? { status } : {} });
  return res.data.data;
}
export async function getOwnerFinanceSurface() {
  const res = await client.get('/api/real_estate/properties/owner-finance-surface');
  return res.data.data;
}
export async function payDeliveryPayable(id, payload) {
  const res = await client.patch(`/api/real_estate/delivery-payables/${id}/pay`, payload);
  return res.data.data;
}
export async function adminListDeliveryPayables(params = {}) {
  const res = await client.get('/api/admin/delivery-payables', { params });
  return res.data.data;
}
export async function adminRemindOwnerPayable(id) {
  const res = await client.post(`/api/admin/delivery-payables/${id}/remind-owner`);
  return res.data.data;
}
export async function adminResolveDeliveryPayable(id, payload) {
  const res = await client.patch(`/api/admin/delivery-payables/${id}`, payload);
  return res.data.data;
}
export async function adminSetDeliveryPayConfig(userId, payload) {
  const res = await client.patch(`/api/admin/real_estate/users/${userId}/delivery-pay`, payload);
  return res.data.data;
}
// Present for the shared DeliveryPoolPricingModal; real_estate uses the fee section only, so
// these are unused in practice but keep the import resolvable.
export async function adminInviteToSharedPool(userId) {
  const res = await client.post(`/api/admin/real_estate/users/${userId}/sharing-invite`);
  return res.data.data;
}
export async function adminSetSharedDeliveryPricing(userId, payload) {
  const res = await client.patch(`/api/admin/real_estate/users/${userId}/shared-delivery-pricing`, payload);
  return res.data.data;
}

// ---- Shared-delivery inter-business billing (finance review only for real_estate; the
// consuming/resource sides are car_rental/jewelry). shared_delivery_inter_business_billing.md ----
export async function adminListInterBusinessCharges(params = {}) {
  const res = await client.get('/api/admin/inter-business-charges', { params });
  return res.data.data;
}
export async function adminRemindInterBusinessOwner(id) {
  const res = await client.post(`/api/admin/inter-business-charges/${id}/remind-owner`);
  return res.data.data;
}
export async function adminResolveInterBusinessCharge(id, payload) {
  const res = await client.patch(`/api/admin/inter-business-charges/${id}`, payload);
  return res.data.data;
}

// ---- Finance / settlements (Owner Bookings & Cancellation v2 doc) ----
// Not vertical-scoped like the admin functions above - settlement config, owner overrides, and
// settlements themselves span all 4 verticals (see rentalapi's routes/ownerCrud.js's
// financeRouter). Real-estate visits themselves are free (no settlement is ever created for a
// visit cancellation), but an admin/finance user signed into this app still needs to manage
// settlements from the other 3 verticals and this vertical's owners' finance overrides.
export async function getSettlementConfig() {
  const res = await client.get('/api/admin/settlement-config');
  return res.data.data;
}

export async function updateSettlementConfig(payload) {
  const res = await client.put('/api/admin/settlement-config', payload);
  return res.data.data;
}

// Owners for the finance-overrides picker. `withOverrides` -> only owners that already have a
// negotiated term; `q` -> free-text name/email search for the lookup typeahead.
export async function adminListOwners({ q, withOverrides } = {}) {
  const params = {};
  if (q) params.q = q;
  if (withOverrides) params.withOverrides = 1;
  const res = await client.get('/api/admin/owners', { params });
  return res.data.data;
}

// Returns the full envelope { data: <overrides>, owner: { _id, name, email } } - a lookup by
// name resolves to an account the caller didn't type, so the UI needs to know which one.
export async function getOwnerFinanceOverrides(ownerId) {
  const res = await client.get(`/api/admin/owners/${encodeURIComponent(ownerId)}/finance-overrides`);
  return res.data;
}

export async function updateOwnerFinanceOverrides(ownerId, payload) {
  const res = await client.put(`/api/admin/owners/${encodeURIComponent(ownerId)}/finance-overrides`, payload);
  return res.data;
}

export async function getSettlements(filters = {}) {
  const res = await client.get('/api/admin/settlements', { params: filters });
  return res.data.data;
}

export async function decideSettlement(id, action, payload = {}) {
  const res = await client.patch(`/api/admin/settlements/${id}/${action}`, payload);
  return res.data.data;
}

// ---- v3_fix_issues #7 & #8 - website-owner business analytics ----
// Platform-wide (not vertical-scoped), same as the settlement functions above. summary =
// KPIs + previous-period deltas + time series + by-vertical / top-customer / top-owner
// breakdowns; ledger = the filterable booking-level revenue list.
export async function getBusinessAnalytics(params = {}) {
  const res = await client.get('/api/admin/analytics/summary', { params });
  return res.data.data;
}

export async function getFinanceLedger(params = {}) {
  const res = await client.get('/api/admin/analytics/ledger', { params });
  return res.data; // { data, pagination, totals }
}

// ---- v3_fix_issues #10 - Request a Demo (public submit + website-owner queue + contact settings) ----
export async function submitDemoRequest(payload) {
  const res = await client.post('/api/demo-requests', payload);
  return res.data;
}

export async function adminListDemoRequests(status) {
  const res = await client.get('/api/admin/demo-requests', { params: status ? { status } : {} });
  return res.data.data;
}

export async function adminUpdateDemoRequest(id, payload) {
  const res = await client.patch(`/api/admin/demo-requests/${id}`, payload);
  return res.data.data;
}

// v9 customer_support_tickets - platform-wide (raised from the homepage, no vertical).
export async function adminListSupportTickets(params = {}) {
  const res = await client.get(`/api/admin/support-tickets`, { params });
  return res.data.data;
}
export async function adminUpdateSupportTicket(id, payload) {
  const res = await client.patch(`/api/admin/support-tickets/${id}`, payload);
  return res.data.data;
}

export async function getPlatformSettings() {
  const res = await client.get('/api/admin/platform-settings');
  return res.data.data;
}

export async function updatePlatformSettings(payload) {
  const res = await client.put('/api/admin/platform-settings', payload);
  return res.data.data;
}

// homepage_refactor.md - "About this business" content (registration, experience, management
// team with photos) for the homepage's Businesses popup. Not vertical-scoped - editable for any
// of the 5 verticals from this same admin login, same reach as platform settings above.
export async function listBusinessProfiles() {
  const res = await client.get('/api/business-profiles');
  return res.data.data;
}

export async function updateBusinessProfile(vertical, payload) {
  const res = await client.put(`/api/admin/business-profiles/${vertical}`, payload);
  return res.data.data;
}

// "Our Team" page content (Docs/requirements/common/homepage/our_team_page.md) - website-owner
// configurable per scope ('platform' = the homepage team; a vertical key = that vertical's own).
export async function getTeamProfile(scope) {
  const res = await client.get(`/api/admin/team-profiles/${scope}`);
  return res.data.data;
}
export async function saveTeamProfile(scope, payload) {
  const res = await client.put(`/api/admin/team-profiles/${scope}`, payload);
  return res.data.data;
}
export async function resetTeamProfile(scope) {
  const res = await client.post(`/api/admin/team-profiles/${scope}/reset`);
  return res.data.data;
}

// ================================================================================================
// v4_fix_issues #2/#4 - real_estate takes no rent; a confirmed visit accrues a flat platform fee
// the property owner owes. (RentalPlatformCharge.)
// ================================================================================================

// Owner's own view of what they owe the platform for confirmed visits.
export async function getOwnerPlatformCharges(status) {
  const res = await client.get('/api/real_estate/platform-charges', { params: status && status !== 'all' ? { status } : {} });
  return res.data.data;
}

// The owner records paying the platform (method + reference, tracking only).
export async function payPlatformCharge(id, payload) {
  const res = await client.patch(`/api/real_estate/platform-charges/${id}/pay`, payload);
  return res.data.data;
}

// Finance queue of visit fees + the decide action (remind / mark_paid / reject / waive).
export async function getPlatformCharges(params = {}) {
  const res = await client.get('/api/admin/platform-charges', { params });
  return res.data.data;
}

export async function decidePlatformCharge(id, payload) {
  const res = await client.patch(`/api/admin/platform-charges/${id}`, payload);
  return res.data.data;
}

// #5 - finance queue of billing-adjustment reconciliations (platform-wide) + the decide action.
// Rarely has real_estate rows (a visit carries no rental amount to adjust) but the queue is
// cross-vertical, so it's shown in every vertical's FinancePanel for consistency.
export async function getReconciliations(params = {}) {
  const res = await client.get('/api/admin/reconciliations', { params });
  return res.data.data;
}

export async function decideReconciliation(id, payload) {
  const res = await client.patch(`/api/admin/reconciliations/${id}`, payload);
  return res.data.data;
}

// Owner Bookings & Cancellation v2 doc, Story 15 - Finance's platform-wide (not vertical-scoped)
// queue of bookings whose deposit refund is still outstanding, and the action to resolve one.
// finance_employee can call getRefundPendingBookings (view-only) but financeMarkRefundSent 403s
// for them - only finance_lead/finance_manager/admin can actually mark one sent.
export async function getRefundPendingBookings() {
  const res = await client.get('/api/admin/refund-pending');
  return res.data.data;
}

export async function financeMarkRefundSent(id, { method, notes } = {}) {
  const res = await client.patch(`/api/admin/refund-pending/${id}`, { method, notes });
  return res.data.data;
}

// payements_requirements.md - Finance's platform-wide queue of owner-recorded payments still
// awaiting confirmation (payment.needsReconciliation), and the action to confirm one. View for all
// finance roles + admin; confirm is finance_lead/finance_manager/admin only.
export async function getPaymentsToReconcile() {
  const res = await client.get('/api/admin/payments-to-reconcile');
  return res.data.data;
}

export async function financeConfirmReconciliation(id, note) {
  const res = await client.patch(`/api/admin/payments-to-reconcile/${id}/confirm`, { note });
  return res.data.data;
}

// ================================================================================================
// Owner onboarding / lead pipeline (Docs/requirements/common/onboard_business_model/
// leads_business_flow_requirement.md) — the website owner's own outbound sales tracker: calls /
// WhatsApp / visits / discussions with a prospective business owner, one paginated page, open/
// close per lead. Admin-only.
// ================================================================================================

export async function listLeads(params = {}) {
  const res = await client.get(`/api/${VERTICAL}/leads`, { params });
  return res.data; // { data, pagination, summary }
}
export async function getLead(id) {
  const res = await client.get(`/api/${VERTICAL}/leads/${id}`);
  return res.data.data;
}
export async function createLead(payload) {
  const res = await client.post(`/api/${VERTICAL}/leads`, payload);
  return res.data.data;
}
export async function updateLead(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/leads/${id}`, payload);
  return res.data.data;
}
export async function setLeadStatus(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/leads/${id}/status`, payload);
  return res.data.data;
}
export async function addLeadInteraction(id, payload) {
  const res = await client.post(`/api/${VERTICAL}/leads/${id}/interactions`, payload);
  return res.data.data;
}
export async function deleteLead(id) {
  const res = await client.delete(`/api/${VERTICAL}/leads/${id}`);
  return res.data.data;
}

// ================================================================================================
// Email log & suppression (email_log_and_suppression requirement) - every email the platform
// sends is logged; a rental owner can stop non-critical emails to one specific customer.
// ================================================================================================

export async function listEmailLog(params = {}) {
  const res = await client.get(`/api/${VERTICAL}/emails`, { params });
  return res.data; // { data, pagination }
}
export async function adminListEmailLog(params = {}) {
  const res = await client.get('/api/admin/emails', { params });
  return res.data;
}
export async function listEmailSuppressions(params = {}) {
  const res = await client.get(`/api/${VERTICAL}/emails/suppressions`, { params });
  return res.data;
}
export async function addEmailSuppression(payload) {
  const res = await client.post(`/api/${VERTICAL}/emails/suppressions`, payload);
  return res.data.data;
}
export async function removeEmailSuppression(id) {
  const res = await client.delete(`/api/${VERTICAL}/emails/suppressions/${id}`);
  return res.data.data;
}
export async function adminListEmailSuppressions(params = {}) {
  const res = await client.get('/api/admin/emails/suppressions', { params });
  return res.data;
}
export async function adminRemoveEmailSuppression(id) {
  const res = await client.delete(`/api/admin/emails/suppressions/${id}`);
  return res.data.data;
}
export async function updateEmailSuppression(id, payload) {
  const res = await client.patch(`/api/${VERTICAL}/emails/suppressions/${id}`, payload);
  return res.data.data;
}
export async function adminUpdateEmailSuppression(id, payload) {
  const res = await client.patch(`/api/admin/emails/suppressions/${id}`, payload);
  return res.data.data;
}
export async function listEmailCategoryBlocks(params = {}) {
  const res = await client.get('/api/admin/emails/category-blocks', { params });
  return res.data;
}
export async function addEmailCategoryBlock(payload) {
  const res = await client.post('/api/admin/emails/category-blocks', payload);
  return res.data.data;
}
export async function updateEmailCategoryBlock(id, payload) {
  const res = await client.patch(`/api/admin/emails/category-blocks/${id}`, payload);
  return res.data.data;
}
export async function removeEmailCategoryBlock(id) {
  const res = await client.delete(`/api/admin/emails/category-blocks/${id}`);
  return res.data.data;
}

// ================================================================================================
// 08_owner_invoice_pdf_export.md - a real_estate rental owner's own manual invoice generator
// (scoped down from the full booking-linked invoicing workflow, see the doc): the owner saves a
// reusable "format" (their business/bank/terms/tax defaults) once, then fills in just the
// customer + line items on each new invoice and exports it to PDF.
// ================================================================================================

// Always resolves - an owner with nothing saved yet gets sensible defaults + a suggested next
// invoice number, so the "New Invoice" and "Invoice Format" forms always have something to render.
export async function getInvoiceTemplate() {
  const res = await client.get('/api/real_estate/invoice-template');
  return res.data.data;
}

export async function saveInvoiceTemplate(payload) {
  const res = await client.put('/api/real_estate/invoice-template', payload);
  return res.data.data;
}

export async function listInvoices() {
  const res = await client.get('/api/real_estate/invoices');
  return res.data.data;
}

export async function createInvoice(payload) {
  const res = await client.post('/api/real_estate/invoices', payload);
  return res.data.data;
}

export async function updateInvoice(id, payload) {
  const res = await client.patch(`/api/real_estate/invoices/${id}`, payload);
  return res.data.data;
}

export async function getAdminInvoiceStats(params = {}) {
  const res = await client.get('/api/admin/real_estate/invoices/stats', { params });
  return res.data;
}

// Owner uploads a business logo (single file) for their saved invoice format - see
// components/InvoicesPanel.js's FormatTab. Returns the stored URL to put straight into
// seller.logoUrl before saving the format (same "upload now, save the URL with the rest of the
// form later" flow as uploadImages() above).
export async function uploadInvoiceLogo(file) {
  const formData = new FormData();
  formData.append('logo', await preparePhoto(file, { maxDimension: 480 }));
  const res = await client.post('/api/uploads/logo', formData);
  return res.data.data.url;
}

// Fetches the generated PDF as a blob - the shared fetch behind both the pre-download Preview
// modal (rendered in an <iframe src={objectURL}>) and the actual save-to-disk below, so opening a
// preview then downloading it doesn't fetch the same PDF twice.
export async function fetchInvoicePdfBlob(id) {
  const res = await client.get(`/api/real_estate/invoices/${id}/pdf`, { responseType: 'blob' });
  return new Blob([res.data], { type: 'application/pdf' });
}

// Triggers a real browser download of an already-fetched PDF blob (no <a download> link works
// directly on the API URL - the request needs the Authorization header the axios interceptor
// attaches, see auth.js - so the blob has to be fetched first either way).
export async function saveInvoicePdfBlob(blob, invoiceNumber) {
  if (isAndroidInvoice()) return saveNativeInvoice(blob, invoiceNumber);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice-${(invoiceNumber || 'draft').replace(/[^a-z0-9-]+/gi, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

// One-shot fetch + save, for call sites that don't need a preview first (e.g. a plain History row
// action).
export async function downloadInvoicePdf(id, invoiceNumber) {
  return saveInvoicePdfBlob(await fetchInvoicePdfBlob(id), invoiceNumber);
}

// manual_vs_automatic_invoicing.md - the owner records that THEIR client paid this invoice (an
// off-platform payment). Moves it to "Paid · awaiting verification"; platform finance verifies
// before it counts toward the owner's revenue.
export async function recordInvoicePayment(id, { method, reference, amount, receivedAt, note }) {
  const res = await client.patch(`/api/real_estate/invoices/${id}/payment`, {
    method, reference,
    ...(amount != null && amount !== '' ? { amount: Number(amount) } : {}),
    ...(receivedAt ? { receivedAt } : {}),
    ...(note ? { note } : {})
  });
  return res.data.data;
}

// The "separate revenue line" for the owner's own invoicing.
export async function getInvoiceSummary(params = {}) {
  const res = await client.get('/api/real_estate/invoices/summary', { params });
  return res.data.data; // { verified, awaitingVerification, unpaid }
}

// PATCH /invoices/:id/status - disable-not-delete: an invoice stays in History either way, just
// badge-marked.
export async function setInvoiceStatus(id, status) {
  const res = await client.patch(`/api/real_estate/invoices/${id}/status`, { status });
  return res.data.data;
}

// Website admin grants/revokes one real_estate owner's ability to generate GST-exempt manual
// invoices (hidden from every owner unless explicitly turned on) - see AdminDashboard.js's Users
// tab.
export async function adminSetInvoicingPermission(userId, allowGstExemptInvoices) {
  const res = await client.patch(`/api/admin/real_estate/users/${userId}/invoicing`, { allowGstExemptInvoices });
  return res.data.data;
}
